import { ComponentType, PropsWithChildren } from "react";
import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { KeycloakProvider } from "@/store/KeycloakProvider.tsx";

type ComponentProps = {
    name?: string;
};

const DEFAULT_STALE_TIME = 1000 * 30;
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            /**
             * 마운트 됐을 때, 기존 데이터가 error 가 있었을때의 재시도 여부. default: true
             */
            retryOnMount: true,
            /**
             * 마운트 됐을 때, 기존 데이터가 stale 상태면 호출함. default: true
             */
            refetchOnMount: true,
            /**
             * 네트워크가 끊겼다가 다시 연결 됐을때, 호출함. default: true
             */
            refetchOnReconnect: true,
            /**
             * 해당 윈도우가 포커스가 나갔다가 다시 잡혔을때 호출함. default: true
             */
            refetchOnWindowFocus: true,
            /**
             * 호출과정에서 error 가 생겼을 때 재시도 여부. error 의 케이스에 따라 다르겠지만 하단 2가지 상황을 가정할 수 있음
             * 1. client 문제(네트워크 연결안됨 등)
             * 2. 백엔드 문제
             *
             * client 의 문제라면 보통 문제가 해결 된 후 새로고침을 하므로 필요없음
             * 백엔드 문제의 경우, 언제 해결 될지 모르므로 필요없음.
             */
            retry: false,
            staleTime: DEFAULT_STALE_TIME,
        },
    },
});

export const persister = createSyncStoragePersister({
    /**
     * 정보를 storage 까지 보관하고 싶으면 하단 코드 사용
     */
    storage: undefined,
});

/**
 * A Higher Order Component that provides the necessary context providers for the app.
 */
function withAppProviders(Component: ComponentType<ComponentProps>) {
    /**
     * The component that wraps the provided component with the necessary context providers.
     */
    function WithAppProviders(props: PropsWithChildren<ComponentProps>) {
        return (
            <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
                <KeycloakProvider>
                    <Component {...props} />
                </KeycloakProvider>
            </PersistQueryClientProvider>
        );
    }

    return WithAppProviders;
}

export default withAppProviders;